import type { Category } from "../api/resourceService";
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useRef } from 'react';
import { getResources } from '../api/resourceService';

export default function SearchBar({
  totalResources,
  categoryList,
  onSearchResults,
}: {
  totalResources: number;
  categoryList: Category[];
  onSearchResults: (resources: any[], total: number, hasFilters: boolean) => void;
}) {
  const { t } = useTranslation();
  const { i18n } = useTranslation();
  const lang = i18n.language.startsWith('en') ? 'en' : 'es';
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isSearching, setIsSearching] = useState(false);
  const [currentResultsCount, setCurrentResultsCount] = useState(totalResources);
  
  // Usar useRef para evitar recrear la función
  const onSearchResultsRef = useRef(onSearchResults);
  onSearchResultsRef.current = onSearchResults;
  
  // Actualizar el contador cuando cambie totalResources
  useEffect(() => {
    setCurrentResultsCount(totalResources);
  }, [totalResources]);
  
  // Función de búsqueda simplificada
  const performSearch = async () => {
    // Si no hay término de búsqueda y es "todas las categorías", mostrar todos
    if (!searchTerm.trim() && selectedCategory === 'all') {
      setCurrentResultsCount(totalResources);
      onSearchResultsRef.current([], totalResources, false); // false = sin filtros
      return;
    }

    // Hay filtros activos: buscar
    setIsSearching(true);
    try {
      const response = await getResources(
        searchTerm.trim() || undefined,
        selectedCategory !== 'all' ? selectedCategory : undefined,
        1, // primera página
        50 // límite alto para mostrar más resultados
      );
      
      // Actualizar el contador con los resultados reales encontrados
      // Si hay búsqueda por texto, usar la longitud de los recursos filtrados
      // Si solo hay filtro de categoría, usar el total de la paginación
      const actualResultsCount = searchTerm.trim() 
        ? response.resources.length 
        : response.pagination.total;
      
      setCurrentResultsCount(actualResultsCount);
      
      // Siempre llamar a onSearchResults con los resultados y flag de filtros activos
      onSearchResultsRef.current(response.resources, response.pagination.total, true); // true = con filtros
    } catch (error) {
      console.error('Error searching resources:', error);
      setCurrentResultsCount(0);
      onSearchResultsRef.current([], 0, true); // true = con filtros (aunque haya error)
    } finally {
      setIsSearching(false);
    }
  };

  // Efecto para búsqueda con debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      performSearch();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedCategory]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedCategory(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setSelectedCategory('all');
    setCurrentResultsCount(totalResources);
    // Limpiar filtros: mostrar todos los recursos
    onSearchResultsRef.current([], totalResources, false);
  };

  // Determinar si hay filtros activos
  const hasActiveFilters = searchTerm.trim() || selectedCategory !== 'all';

  return (
    <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-lg mb-8 p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        {/* Barra de búsqueda */}
        <div className="relative flex-grow">
          <span className="material-icons absolute left-3 top-3 text-[var(--text-muted)] text-lg pointer-events-none">
            search
          </span>
          <input
            className="w-full pl-10 pr-4 py-3 border border-[var(--border)] rounded-lg bg-[var(--surface)] text-[var(--text-default)] placeholder-[var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--border)] focus:border-transparent transition-all duration-200"
            placeholder={t('search.placeholder')}
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
          />
          {searchTerm && (
            <button
              onClick={handleClearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-default)] transition-colors duration-200"
              aria-label={t('search.clearSearch')}
            >
              <span className="material-icons text-lg">close</span>
            </button>
          )}
        </div>

        {/* Controles de filtro y estadísticas */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
          {/* Selector de categoría */}
          <div className="relative w-full sm:w-auto">
            <select
              className="appearance-none w-full sm:w-48 bg-[var(--surface)] border border-[var(--border)] text-[var(--text-default)] text-sm rounded-lg focus:ring-2 focus:ring-[var(--border)] focus:border-transparent block p-3 pr-10 transition-all duration-200"
              value={selectedCategory}
              onChange={handleCategoryChange}
            >
              <option value="all">{t('search.allCategories')}</option>
              {categoryList?.map(c => (
                <option key={c.id} value={c.id}>{c.name[lang]}</option>
              ))}
            </select>
            <span className="material-icons absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--text-muted)] pointer-events-none text-lg">
              expand_more
            </span>
          </div>

          {/* Contador de recursos */}
          <div className="flex items-center gap-2 text-sm">
            {isSearching ? (
              <div className="flex items-center gap-2 text-[var(--text-muted)]">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--text-default)]"></div>
                <span>{t('search.searching')}</span>
              </div>
            ) : (
              <span className="text-[var(--text-muted)]">
                {hasActiveFilters ? (
                  <>
                    <span className="font-bold text-[var(--text-default)]">
                      {t('search.resultsFound', { count: currentResultsCount })}
                    </span>
                    <button
                      onClick={handleClearSearch}
                      className="ml-2 text-[var(--text-muted)] hover:text-[var(--surface)] hover:bg-[var(--border)] transition-colors duration-200 p-1 rounded-lg"
                      title={t('search.clearFilters')}
                      aria-label={t('search.clearFilters')}
                    >
                      <span className="material-icons text-sm">cleaning_services</span>
                    </button>
                  </>
                ) : (
                  <span className="font-bold text-[var(--text-default)]">
                    {t('search.totalResources', { count: totalResources })}
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
